import {useEffect, useMemo, useState} from "react";
import moment from "moment";
import VLFSelectNew from "@/components/Input/SelectNew";
import MainActions from "@/components/MainActions";
import {getInternalClubId} from "@/utils/constants";
import {MatchAPI} from "@/api/match";
import {PlayerAPI} from "@/api/player";
import {certificateNote, certificateStatus} from "@/utils/certificates";
import ConvocationSheet from "./ConvocationSheet";
import AddIcon from "@mui/icons-material/Add";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";

import "./ConvocationsTab.scss";

// ── Role badge (mirrors handleRoster.js) ─────────────────────────────────────

const ROLE_BADGE = {
	Portiere: {short: "POR", cls: "gk"},
	Difensore: {short: "DIF", cls: "def"},
	Mediano: {short: "MED", cls: "dmid"},
	Centrocampista: {short: "CEN", cls: "mid"},
	Trequartista: {short: "TRE", cls: "amid"},
	Attaccante: {short: "ATT", cls: "att"},
};

const ROLE_ORDER = {
	Portiere: 1,
	Difensore: 2,
	Mediano: 3,
	Centrocampista: 4,
	Trequartista: 5,
	Attaccante: 6,
};

const getRoleBadge = (p) =>
	p.goalkeeper
		? ROLE_BADGE.Portiere
		: ROLE_BADGE[p.primaryRole] ?? {short: "—", cls: "default"};

export const getRoleOrder = (p) =>
	p.goalkeeper ? 1 : ROLE_ORDER[p.primaryRole] ?? 99;

// ── Status config ─────────────────────────────────────────────────────────────

// "Presente" non si sceglie qui: lo registra la distinta (o l'RPE del giocatore).
const STATUSES = [
	{value: "Convocato", label: "Convocato", cls: "called"},
	{value: "Assente", label: "Assente", cls: "absent"},
	{value: "Infortunato", label: "Infortunato", cls: "injured"},
];
const STATUS_CLS = {
	Presente: "present",
	Convocato: "called",
	Assente: "absent",
	Infortunato: "injured",
};

// ── Summary strip ─────────────────────────────────────────────────────────────

function SummaryStrip({groups}) {
	const counts = {
		Presente: groups.Presente.length,
		Convocato: groups.Convocato.length,
		Assente: groups.Assente.length,
		Infortunato: groups.Infortunato.length,
		none: groups.none.length,
	};

	return (
		<div className="cv-summary">
			<span className="cv-summary__item cv-summary__item--present">
				<span className="cv-summary__dot" />
				{counts.Convocato} convocati · {counts.Presente} in distinta
			</span>
			<span className="cv-summary__sep">·</span>
			<span className="cv-summary__item cv-summary__item--absent">
				<span className="cv-summary__dot" />
				{counts.Assente} assenti
			</span>
			<span className="cv-summary__sep">·</span>
			<span className="cv-summary__item cv-summary__item--injured">
				<span className="cv-summary__dot" />
				{counts.Infortunato} infortunati
			</span>
			<span className="cv-summary__sep">·</span>
			<span className="cv-summary__item cv-summary__item--none">
				<span className="cv-summary__dot" />
				{counts.none} da definire
			</span>
		</div>
	);
}

// ── Player row ────────────────────────────────────────────────────────────────

export function PlayerRow({
	player,
	statuses = STATUSES,
	groupLabel = "Convocazione",
	currentStatus,
	attendanceId,
	onStatusClick,
	busy,
	otherRoster,
	participation,
	medicalInjury,
	certificateWarning,
}) {
	const badge = getRoleBadge(player);
	const year = player.dateOfBirth
		? moment(player.dateOfBirth).format("YYYY")
		: player.playerYear;

	return (
		<div
			role="group"
			aria-label={`${groupLabel} ${player.playerName || `${player.firstName || ""} ${player.lastName || ""}`}`}
			className={
				"cv-row" +
				(currentStatus ? ` cv-row--${STATUS_CLS[currentStatus] || ""}` : "")
			}
		>
			{/* Role badge */}
			<span className={`cv-role-badge cv-role-badge--${badge.cls}`}>
				{badge.short}
			</span>

			{/* Name + year */}
			<div className="cv-row__info">
				<span className="cv-row__name">
					{player.playerName ||
						`${player.firstName || ""} ${player.lastName || ""}`}
				</span>
				{year && <span className="cv-row__year">{year}</span>}
				{participation?.participationId && (
					<span className="cv-row__other-roster">
						N° {participation.playerNumber} ·{" "}
						{participation.startingPlayer
							? participation.hasPlayed === false
								? "Titolare · non ha giocato"
								: "Titolare"
							: participation.hasPlayed
								? "Riserva · entrato"
								: "Riserva · non entrato"}
					</span>
				)}
				{otherRoster && (
					<span className="cv-row__other-roster">Altra rosa</span>
				)}
				{medicalInjury && (
					<span className="cv-row__other-roster">
						Infortunio attivo · area medica
					</span>
				)}
				{certificateWarning && (
					<span className="cv-row__other-roster cv-row__other-roster--critical">
						{certificateWarning}
					</span>
				)}
			</div>

			{/* Status buttons */}
			<div className="cv-row__actions">
				{participation?.participationId ? (
					<span className="cv-row__in-lineup">In distinta</span>
				) : (
					statuses.map((s) => {
						const isActive = currentStatus === s.value;
						return (
							<button
								key={s.value}
								disabled={busy}
								className={
									`cv-status-btn cv-status-btn--${s.cls}` +
									(isActive ? " cv-status-btn--active" : "")
								}
								onClick={() =>
									onStatusClick(player.id, s.value, attendanceId, isActive)
								}
							>
								{s.label}
							</button>
						);
					})
				)}
			</div>

			{busy && <span className="cv-row__saving">…</span>}
		</div>
	);
}

// ── Section header ────────────────────────────────────────────────────────────

export function SectionHeader({label, count, cls}) {
	return (
		<div className={`cv-section-header cv-section-header--${cls}`}>
			<span className="cv-section-header__label">{label}</span>
			<span className="cv-section-header__count">{count}</span>
		</div>
	);
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ConvocationsTab({
	matchData,
	teamPlayers,
	onAddPlayers,
	onStatusClick, // (playerId, newStatus, attendanceId, isSameStatus) → Promise
	injuredIds,
}) {
	const [selectedSide, setSelectedSide] = useState("home");
	const sides = [
		{
			value: "home",
			label: matchData?.homeTeamName || matchData?.homeClub,
			clubId: matchData?.homeClubId,
		},
		{
			value: "away",
			label: matchData?.awayTeamName || matchData?.awayClub,
			clubId: matchData?.awayClubId,
		},
	].filter(
		(s) =>
			s.clubId === getInternalClubId() &&
			(!matchData?.participationSides ||
				matchData.participationSides.includes(s.value)),
	);
	const side =
		sides.find((s) => s.value === selectedSide)?.value ||
		sides[0]?.value ||
		"home";
	const [roster, setRoster] = useState(null);
	const [rosterLoading, setRosterLoading] = useState(true);
	const [rosterError, setRosterError] = useState("");
	const [reload, setReload] = useState(0);
	const rosterId =
		side === "home" ? matchData?.homeRosterId : matchData?.awayRosterId;
	useEffect(() => {
		let active = true;
		setRoster(null);
		setRosterError("");
		setRosterLoading(true);
		MatchAPI.getLineupRoster(matchData.id, side)
			.then((data) => {
				if (active) setRoster(data);
			})
			.catch((error) => {
				if (active)
					setRosterError(
						error?.response?.data?.error ||
							"Impossibile caricare i convocabili della rosa.",
					);
			})
			.finally(() => {
				if (active) setRosterLoading(false);
			});
		return () => {
			active = false;
		};
	}, [matchData.id, side, rosterId, reload]);
	const hasRosterDistinction = !!roster?.rosterId;
	const matchRosterPlayerIds = useMemo(
		() =>
			new Set(
				(roster?.players || [])
					.filter(
						(p) => !p.sourceRosterId || p.sourceRosterId === roster.rosterId,
					)
					.map((p) => p.playerId),
			),
		[roster],
	);
	const isOtherRoster = (p) =>
		hasRosterDistinction && !matchRosterPlayerIds.has(p.id);

	// Track which player rows are currently saving
	const [busyIds, setBusyIds] = useState(new Set());
	const [openSheet, setOpenSheet] = useState(false);

	// Build playerId → {attendanceId, status} from matchConvocations
	const convMap = useMemo(() => {
		const map = {};
		for (const c of matchData?.matchConvocations ?? []) {
			if (c.matchSide && c.matchSide !== side) continue;
			map[c.playerId] = {
				...c,
				attendanceId: c.id,
				status: c.status === "convocato" ? "Convocato" : c.status,
			};
		}
		return map;
	}, [matchData?.matchConvocations, side]);

	const availablePlayers = useMemo(() => {
		const map = new Map((teamPlayers || []).map((p) => [p.id, p]));
		for (const p of roster?.players || [])
			map.set(p.playerId, {
				...map.get(p.playerId),
				...p,
				id: p.playerId,
				primaryRole: p.role || map.get(p.playerId)?.primaryRole,
				roster: {id: p.sourceRosterId || roster.rosterId},
			});
		for (const p of matchData?.matchPlayers || []) {
			if (!p.playerId || p.matchSide !== side) continue;
			map.set(p.playerId, {
				...p.player,
				...map.get(p.playerId),
				id: p.playerId,
				playerName: p.playerName || map.get(p.playerId)?.playerName,
				roster: {
					id:
						p.sourceRosterId ||
						convMap[p.playerId]?.rosterId ||
						map.get(p.playerId)?.roster?.id,
				},
			});
		}
		for (const c of Object.values(convMap)) {
			if (!map.has(c.playerId))
				map.set(c.playerId, {
					id: c.playerId,
					playerName: "Nome non disponibile",
					roster: {id: c.rosterId},
				});
		}
		return [...map.values()];
	}, [teamPlayers, roster, matchData?.matchPlayers, side, convMap]);
	// Certificato medico-sportivo alla data della gara: chi lo ha scaduto non è impiegabile
	const [certificates, setCertificates] = useState({});
	const playerIdsKey = availablePlayers
		.map((p) => p.id)
		.sort()
		.join(",");
	useEffect(() => {
		if (!playerIdsKey) return;
		let active = true;
		Promise.all(
			playerIdsKey.split(",").map((id) =>
				PlayerAPI.getCertificates(id)
					.then((certs) => [id, certs || []])
					.catch(() => [id, null]),
			),
		).then((rows) => {
			if (active) setCertificates(Object.fromEntries(rows));
		});
		return () => {
			active = false;
		};
	}, [playerIdsKey]);
	const certificateWarningFor = (playerId) => {
		const certs = certificates[playerId];
		// Senza certificati inseriti non si segnala nulla: il club può non usarli
		if (!certs?.length) return null;
		const res = certificateStatus(certs, matchData?.startDate || undefined);
		return res.status === "expired"
			? "Certificato medico " + certificateNote(res).toLowerCase()
			: null;
	};

	const sortedPlayers = useMemo(
		() =>
			availablePlayers
				.filter((p) => matchRosterPlayerIds.has(p.id) || !!convMap[p.id])
				.sort((a, b) => {
					const group =
						Number(!matchRosterPlayerIds.has(a.id)) -
						Number(!matchRosterPlayerIds.has(b.id));
					if (hasRosterDistinction && group) return group;
					const role = getRoleOrder(a) - getRoleOrder(b);
					return (
						role ||
						(a.lastName || a.playerName || "").localeCompare(
							b.lastName || b.playerName || "",
						)
					);
				}),
		[availablePlayers, hasRosterDistinction, matchRosterPlayerIds, convMap],
	);

	// Group into sections
	const groups = useMemo(() => {
		const g = {
			Presente: [],
			Convocato: [],
			Assente: [],
			Infortunato: [],
			none: [],
		};
		for (const p of sortedPlayers) {
			const entry = convMap[p.id];
			const status =
				entry?.status === "convocato"
					? "Convocato"
					: entry?.status ?? (injuredIds?.has(p.id) ? "Infortunato" : "none");
			(g[status] || g.none).push(p);
		}
		return g;
	}, [sortedPlayers, convMap, injuredIds]);

	const handleClick = async (
		playerId,
		newStatus,
		attendanceId,
		isSameStatus,
	) => {
		setBusyIds((prev) => new Set(prev).add(playerId));
		try {
			// Clicking the already-active status removes the convocation
			await onStatusClick(
				playerId,
				isSameStatus ? null : newStatus,
				attendanceId,
				side,
				availablePlayers.find((p) => p.id === playerId)?.roster?.id || null,
			);
		} finally {
			setBusyIds((prev) => {
				const next = new Set(prev);
				next.delete(playerId);
				return next;
			});
		}
	};

	const renderGroup = (players, status, label, cls) => {
		if (players.length === 0 && status !== "none") return null;
		return (
			<div key={status} className="cv-group">
				<SectionHeader label={label} count={players.length} cls={cls} />
				<div className="cv-group__body">
					{players.length === 0 ? (
						<div className="cv-group__empty">Nessuno</div>
					) : (
						players.map((p) => (
							<PlayerRow
								key={p.id}
								player={p}
								currentStatus={convMap[p.id]?.status ?? null}
								attendanceId={convMap[p.id]?.attendanceId}
								onStatusClick={handleClick}
								busy={busyIds.has(p.id)}
								otherRoster={convMap[p.id]?.outOfRoster ?? isOtherRoster(p)}
								participation={convMap[p.id]}
								medicalInjury={
									injuredIds?.has(p.id) &&
									convMap[p.id]?.status !== "Infortunato"
								}
								certificateWarning={certificateWarningFor(p.id)}
							/>
						))
					)}
				</div>
			</div>
		);
	};

	return (
		<div className="cv-wrap">
			{sides.length > 1 && (
				<VLFSelectNew
					label="Squadra"
					options={sides}
					value={sides.find((s) => s.value === side)}
					onChange={(s) => setSelectedSide(s.value)}
					isClearable={false}
				/>
			)}
			<div className="cv-roster-controls">
				<span>
					{roster?.rosterName
						? `Convocabili: ${roster.rosterName}`
						: "Convocabili della partita"}
				</span>
				<MainActions
					actions={[
						onAddPlayers && {
							icon: <AddIcon />,
							title: "Aggiungi convocati",
							variant: "outlined",
							onClick: () => onAddPlayers(side),
						},
						{
							icon: <CampaignOutlinedIcon />,
							label: "Convocazione",
							variant: "outlined",
							onClick: () => setOpenSheet(true),
						},
					].filter(Boolean)}
				/>
			</div>
			<ConvocationSheet
				open={openSheet}
				close={() => setOpenSheet(false)}
				matchData={matchData}
				side={side}
				rosterId={roster?.rosterId || rosterId}
				clubName={side === "home" ? matchData?.homeClub : matchData?.awayClub}
				teamName={
					roster?.rosterName ||
					(side === "home" ? matchData?.homeTeamName : matchData?.awayTeamName)
				}
				players={[...groups.Convocato, ...groups.Presente]}
			/>
			{rosterLoading && <p role="status">Caricamento convocabili...</p>}
			{rosterError && (
				<div role="alert">
					{rosterError}{" "}
					<button type="button" onClick={() => setReload((n) => n + 1)}>
						Riprova
					</button>
				</div>
			)}
			<SummaryStrip groups={groups} />

			{renderGroup(groups.Convocato, "Convocato", "Convocati", "called")}
			{renderGroup(groups.Presente, "Presente", "In distinta", "present")}
			{renderGroup(groups.Assente, "Assente", "Assenti", "absent")}
			{renderGroup(groups.Infortunato, "Infortunato", "Infortunati", "injured")}
			{renderGroup(groups.none, "none", "Da definire", "none")}
		</div>
	);
}
